/**
 * User store.
 * @author          David Gustavssonb
 * @description     This store will handle all logic related to the active user.
 */
'use strict';

import { addMinutes, isBefore } from 'date-fns';

import {
  GetCompanyResponse,
  UserCompaniesResponse,
} from '../../../typings/api/skymap/rest/v0/company';
import { SkyMapAxiosServiceFactory } from '../services/axios/skymap-axios-service-factory';
import { isDefined } from '../utils/variables';

export type UserCompanies = UserCompaniesResponse['data'];

const cacheExpirationInMinutes = 30;

export class CompanyStore {
  static get instance() {
    return CompanyStore._instance;
  }

  private constructor() {
    /*  */
  }

  private companyInfoLastFetched: Date | undefined;
  private _company?: GetCompanyResponse;
  get company() {
    if (!isDefined(this._company)) {
      throw Error('Company is not loaded.');
    }

    return this._company;
  }

  reset() {
    this._company = undefined;
  }

  isCacheActive() {
    return (
      isDefined(this.companyInfoLastFetched) &&
      isBefore(new Date(), addMinutes(this.companyInfoLastFetched, cacheExpirationInMinutes))
    );
  }

  updateCacheTime() {
    this.companyInfoLastFetched = new Date();
  }

  async fetchCompanyInfo(companyId: string): Promise<GetCompanyResponse> {
    if (this.isCacheActive() && this._company?.id === companyId) {
      return this._company;
    }

    this.reset();
    const response = await SkyMapAxiosServiceFactory.instance.createCompanyServiceV0().getCompany({
      path: {
        companyId,
      },
    });

    this.updateCacheTime();
    this._company = response;
    return response;
  }

  private static _instance: CompanyStore = new CompanyStore();
  private user?: { id: string; organizationAdmin: boolean };
  private companies?: UserCompanies;

  async fetchCompanies(userId = this.user?.id, organizationAdmin = false): Promise<void> {
    if (!userId) {
      this.companies = undefined;
      return;
    }

    this.user = { id: userId, organizationAdmin };

    if (organizationAdmin) {
      this.companies = [];
    }

    const companyService = SkyMapAxiosServiceFactory.instance.createCompanyServiceV0();
    const { data: companies } = await companyService.fetchUserCompanies({ query: { id: userId } });

    this.companies = companies;
  }

  getCompanies() {
    return this.companies;
  }

  isCompaniesLoaded() {
    return !!this.companies;
  }

  getCompanyCountry(companyId: string) {
    return this.companies?.find((company) => company.id === companyId)?.country;
  }

  isCompanyAdmin(projectId?: string): boolean {
    if (!isDefined(this.companies) || this.companies.length === 0) {
      return false;
    }

    if (!projectId) {
      return true;
    }

    return this.companies.some((company) =>
      company.Projects.some((project) => project.id === projectId && project.status === 'ACTIVE'),
    );
  }
}
